<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AddFixedAssetToRentalModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    asset: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_5534025912102772_auto = computed(() => false);
  const b_5731695935601903_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5534025912102772_auto: b_5534025912102772_auto.value,
    b_5731695935601903_dataSource: b_5731695935601903_dataSource.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateRentalAsset', {
      asset: props.asset,
      valid_from: $fn.value.today(),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5534025912102772_auto', nodeId: '5534025912102772' },
      { name: 'b_5731695935601903_dataSource', nodeId: '5731695935601903' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5534025912102772_auto,
      b_5731695935601903_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('AddFixedAssetToRentalModal', 'Add to Rental'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.asset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('asset');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_pgmwbrob9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_fr4w78bfh_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_2wyzmdim4_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_rental_asset',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_pgmwbrob9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_pgmwbrob9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ukmkcgj7g =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_pgmwbrob9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ukmkcgj7g],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_6ueblctrd =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_n3q0ibmde =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_6ueblctrd,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_n3q0ibmde],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_pbw8arfem =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_9izkq1w3j =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_pbw8arfem,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_9izkq1w3j],
    event: 'close',
  });
  const act_1040167445267876_closeModal_fr4w78bfh =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_fr4w78bfh_getActionArgs,
    });
  const inst_evh_1040167445267876_success_ecfrj85u2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_fr4w78bfh,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_2wyzmdim4 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_2wyzmdim4_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_ecfrj85u2],
  });
  const inst_evh_1040167445267876_submit_a15yv476a =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_2wyzmdim4,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_a15yv476a],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_97kfk67wm =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_b527416zx =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_97kfk67wm,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_b527416zx],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ukmkcgj7g,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_n3q0ibmde,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_9izkq1w3j,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_ecfrj85u2,
    inst_evh_1040167445267876_submit_a15yv476a,
    submit: inst_evh_1040167445267876_submit_a15yv476a,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_b527416zx,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ukmkcgj7g = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ukmkcgj7g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_n3q0ibmde = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_n3q0ibmde.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_9izkq1w3j = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_9izkq1w3j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ecfrj85u2 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ecfrj85u2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_a15yv476a = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_a15yv476a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_b527416zx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_b527416zx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AddFixedAssetToRentalModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="addFixedAssetToRental"
        :id="formId"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol :auto="b_5534025912102772_auto">
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="rental_id"
            :label="pgettext('AddFixedAssetToRentalModal', 'Rental')"
          >
            <ItpFormSearchableSelect
              name="parent_id"
              v-model="item.rental_id"
              :dataSource="b_5731695935601903_dataSource"
              valueField="id"
              textField="name"
              use-option-template-for-selected-option
              optionTemplate='<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>'
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="valid_from"
            :label="pgettext('AddFixedAssetToRentalModal', 'Valid From')"
          >
            <ItpFormDatetime
              name="valid_from"
              v-model="item.valid_from"
              format="YYYY-MM-DD"
              valueType="format"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="valid_to"
            :label="pgettext('AddFixedAssetToRentalModal', 'Valid To')"
          >
            <ItpFormDatetime
              name="valid_to"
              v-model="item.valid_to"
              format="YYYY-MM-DD"
              valueType="format"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="valid_to"
            :label="pgettext('AddFixedAssetToRentalModal', 'Notes')"
          >
            <ItpFormTextarea name="notes" v-model="item.notes">
            </ItpFormTextarea>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          type="submit"
          :text="pgettext('AddFixedAssetToRentalModal', 'Add')"
          :form="forms.addFixedAssetToRental"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
